/* eslint-disable no-undef */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { TextField } from '../../components/TextFieldInput';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { TextArea } from '../../components/TextAreaInput';
import { Drawer } from '../../components/Drawer';
import { Button } from '../../components/Button';
import { SearchInput } from '../../components/SearchInput';
import { Map } from '../../components/map/Map';
import { Loader } from '@googlemaps/js-api-loader';
import { buffer, point, centroid } from '@turf/turf';
import RemoveDialog from '../../components/RemoveDialog';
import { AreasContext } from '../../contexts/AreasContext';
import MetadataMenu from '../../components/MetadataMenu';
import { MiniCard } from '../../components/MiniCard';
import compareStrings from '../../utils/compareStrings';
import { CardHeader } from '../../components/CardHeader';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';
import { usePermissions } from '../../contexts/PermissionsContext';
import { AddButton } from '../../components/AddButton';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { CircularProgress } from '@material-ui/core';
import { v4 } from 'uuid';

const LABEL_SIZE = 70;

function getSchema(intl) {
  return Yup.object().shape({
    name: Yup.string()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .matches(
        /^[!-ÿ\s]*$/,
        intl.formatMessage({ id: 'ADMINISTRATION.NAME.REGEX' })
      ),
    description: Yup.string().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
    externalId: Yup.string()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .matches(
        /^[a-z0-9-.]*$/,
        intl.formatMessage({ id: 'ADMINISTRATION.CODE.REGEX' })
      ),
    googleFormattedAddress: Yup.string().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
    googlePlaceId: Yup.string().required(
      intl.formatMessage({ id: 'ACTIONS.SELECT_PLACE' })
    ),
    geoJson: Yup.object().required(
      intl.formatMessage({ id: 'ACTIONS.DRAW_GEOFENCE' })
    ),
  });
}

function AreasEdit({ intl, history }) {
  let { id } = useParams();
  const initialValues = {
    name: '',
    googleFormattedAddress: '',
    description: '',
    externalId: v4(),
    googlePlaceId: '',
    street: '',
    locality: '',
    adminRegion: '',
    postalCode: '',
    'countryISO3166-2': '',
  };
  const [isLoading, updateState] = useState(false);
  const [area, setArea] = useState(initialValues);
  const [facilities, setFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [open, setOpen] = useState(false);
  const [places, setPlaces] = useState(null);
  const [controller, setController] = useState(null);
  const [menuOpen, setMenuOpen] = useState({
    facilities: false,
    metadatas: false,
  });
  const { getAreas } = useContext(AreasContext);
  const { hasPermission } = usePermissions();
  const { setPaths } = useBreadcrumb();
  const mapRef = useRef(null);

  if (id === 'new') id = null;

  useEffect(() => {
    async function getArea() {
      updateState(true);
      if (id) {
        try {
          const [areasPromise, facilitiesPromise] = await Promise.all([
            axios.get(`/areas/${id}`),
            axios.get(`/areas/${id}/facilities`),
          ]);
          if (areasPromise.data) {
            setArea(areasPromise.data);
            document.getElementById('googleFormattedAddress').value =
              areasPromise.data.googleFormattedAddress;
            setPaths([
              {
                title: intl.formatMessage({ id: 'AREAS.TITLE' }),
                pathname: '/geo-planner/areas',
              },
              {
                title: areasPromise.data.name,
                pathname: `/geo-planner/areas/${id}`,
              },
            ]);
          }
          if (facilitiesPromise.data) {
            setFacilities(facilitiesPromise.data);
            setFilteredFacilities(facilitiesPromise.data);
          }
        } finally {
          updateState(false);
        }
      } else {
        setArea(initialValues);
        updateState(false);
      }
    }
    getArea();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (controller && mapRef.current) {
      if (!id) {
        controller.clear();
        mapRef.current.jumpTo({
          center: [0, 0],
          zoom: 2,
        });
        document.getElementById('googleFormattedAddress').value = '';
        formik.resetForm();
      }
      if (area.id) {
        const coords = JSON.parse(area.mapCentroid).coordinates;
        mapRef.current.jumpTo({
          center: [coords[1], coords[0]],
          zoom: area.zoomLevel,
        });
        controller.add(area.geoJson);
        controller.calculateArea(area.geoJson);
      }
    }
  }, [controller, area, mapRef]);

  useEffect(() => {
    const loader = new Loader({
      apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
      language: 'en',
      libraries: ['places'],
    });
    loader.load().then(() => {
      const maps = new google.maps.places.Autocomplete(
        document.getElementById('googleFormattedAddress')
      );
      setPlaces(maps);
    });
  }, []);

  async function getCoordinatesFromGoogle(value) {
    formik.setFieldValue('googleFormattedAddress', value);
    formik.setFieldValue('googlePlaceId', '');

    google.maps.event.addListener(places, 'place_changed', () => {
      const place = places.getPlace();
      const latitude = place.geometry.location.lat();
      const longitude = place.geometry.location.lng();
      mapRef.current.flyTo({
        center: [longitude, latitude],
        zoom: 16,
        essential: true,
      });
      mapRef.current.resize();
      const geofence = buffer(point([longitude, latitude]), 0.03);
      geofence.properties = {
        isCircle: true,
        radiusInKm: 0.03,
        center: [longitude, latitude],
      };
      updateGeofenceGoogleInfo(place);
      formik.setFieldValue('googleFormattedAddress', place.formatted_address);
      formik.setFieldValue('googlePlaceId', place.place_id);
      formik.setFieldValue('geoJson', geofence);
      if (controller) {
        controller.deleteAll();
        controller.add(geofence);
        controller.calculateArea(geofence);
      }
    });
  }

  function updateGeofenceGoogleInfo(place) {
    place.address_components.forEach(addressComponent => {
      const componentLong = addressComponent.long_name;
      const componentShort = addressComponent.short_name;
      if (addressComponent.types[0] === 'postal_code') {
        formik.setFieldValue('postalCode', componentLong || '');
      } else if (addressComponent.types[0] === 'country') {
        formik.setFieldValue('countryISO3166-2', componentShort || '');
      } else if (addressComponent.types[0] === 'route') {
        formik.setFieldValue('street', componentLong || '');
      } else if (addressComponent.types[0] === 'administrative_area_level_2') {
        formik.setFieldValue('locality', componentLong || '');
      } else if (addressComponent.types[0] === 'administrative_area_level_1') {
        formik.setFieldValue('adminRegion', componentLong || '');
      }
    });
  }

  const formik = useFormik({
    initialValues: area,
    enableReinitialize: true,
    validationSchema: getSchema(intl),
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setSubmitting(true);
      delete values.areaCentroid;
      values.zoomLevel = mapRef.current.getZoom();
      const coords = centroid(values.geoJson.geometry).geometry.coordinates;
      values.mapCentroid = {
        lat: coords[1],
        long: coords[0],
      };
      if (!id) {
        const { data } = await axios.post(`/areas`, values);
        await getAreas();
        history.push(`/geo-planner/areas/${data.id}`);
      } else {
        delete values.id;
        await axios.put(`/areas/${id}`, values);
        await getAreas();
        setPaths([
          {
            title: intl.formatMessage({ id: 'AREAS.TITLE' }),
            pathname: '/geo-planner/areas',
          },
          { title: values.name, pathname: `/geo-planner/areas/${id}` },
        ]);
      }
      setSubmitting(false);
    },
  });

  function closeSidebar() {
    setMenuOpen({ facilities: false, metadatas: false });
  }

  function closeModal() {
    setOpen(false);
  }

  function changeText(val) {
    setFilteredFacilities(
      facilities.filter(x => compareStrings(JSON.stringify(x), val))
    );
  }

  async function removeArea() {
    closeModal();
    try {
      updateState(true);
      await axios.delete(`/areas/${id}`);
      await getAreas();
      history.push('/geo-planner/areas');
    } finally {
      updateState(false);
    }
  }

  return (
    <div className="card flex-row-fluid h-100">
      <div
        className={`${isLoading ? 'h-100 w-100 d-flex flex-center' : 'd-none'}`}
      >
        <CircularProgress color="secondary" />
      </div>
      <div className={`${isLoading ? 'd-none' : ''}`}>
        <div className="my-4">
          <div className="d-flex flex-column flex-lg-row">
            <div className="col-sm-12 col-md-12 col-lg-7 col-xl-7">
              <TextField
                labelClasses="font-weight-bolder"
                field="externalId"
                inputSize="60%"
                labelMinSize={LABEL_SIZE}
                formik={formik}
                label="ID"
              />
              <TextField
                labelClasses="font-weight-bolder"
                field="name"
                inputSize="100%"
                labelMinSize={LABEL_SIZE}
                formik={formik}
                label={intl.formatMessage({ id: 'AREA_NAME' })}
              />
            </div>
            <div className="col-sm-12 col-md-12 col-lg-5 col-xl-5">
              <TextArea
                labelClass="font-weight-bolder"
                field="description"
                height={100}
                inputSize="100%"
                direction="mt-2"
                labelMinSize={LABEL_SIZE}
                formik={formik}
                label={intl.formatMessage({ id: 'DESCRIPTION' })}
              />
            </div>
          </div>
          <div className="col-sm-12 col-md-12 col-lg-7 col-xl-7">
            <div className="form-group fv-plugins-icon-container">
              <div className="d-flex align-items-center">
                <label
                  style={{ minWidth: LABEL_SIZE }}
                  className="font-weight-bolder"
                >
                  {intl.formatMessage({
                    id: 'ADMINISTRATION.BUSINESS.CREATE.ADDRESS',
                  })}
                </label>
                <input
                  autoComplete="on"
                  onFocus={() =>
                    formik.setFieldTouched('googleFormattedAddress', true)
                  }
                  id="googleFormattedAddress"
                  style={{ width: '100%' }}
                  className={`form-control form-control-solid h-auto py-3 px-4`}
                  defaultValue={formik.values.googleFormattedAddress}
                  onChange={e => getCoordinatesFromGoogle(e.target.value)}
                />
              </div>
              {formik.touched['googleFormattedAddress'] &&
              formik.errors['googlePlaceId'] ? (
                <div className="d-flex">
                  <label style={{ minWidth: LABEL_SIZE, maxWidth: LABEL_SIZE }}>
                    {' '}
                  </label>
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      {formik.errors['googlePlaceId']}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <div className="col-12 d-flex justify-content-between">
            <div className="d-flex">
              {hasPermission('areas*', 'U') && (
                <Button
                  classes="w-75px px-12 py-2 my-1 d-flex align-items-center justify-content-center btn btn-primary"
                  type="submit"
                  formik={formik}
                  onClick={formik.handleSubmit}
                  title={intl.formatMessage({ id: 'SAVE' })}
                />
              )}
              <Button
                classes="w-75px cancel-bg ml-3 cancel-btn cancel-text-color px-4"
                type="button"
                onClick={() => history.push(`/geo-planner/areas`)}
                title={intl.formatMessage({ id: 'CANCEL' })}
              />
              {id && hasPermission('areas*', 'D') && (
                <Button
                  classes="w-75px bg-danger ml-3 px-4"
                  type="button"
                  onClick={() => setOpen(true)}
                  title={intl.formatMessage({ id: 'DELETE' })}
                />
              )}
            </div>
            <div>
              {hasPermission('metadata*', 'R') && (
                <Button
                  classes="px-5"
                  logoSrc={'/media/svg/senseworks/metadata.svg'}
                  disabled={!id ? true : false}
                  type="button"
                  onClick={() =>
                    setMenuOpen({ facilities: false, metadatas: true })
                  }
                  title={intl.formatMessage({ id: 'METADATA' })}
                />
              )}
              {hasPermission('facilities*', 'R') && (
                <Button
                  classes="px-5 ml-3"
                  logoSrc={'/media/svg/senseworks/facility.svg'}
                  disabled={!id ? true : false}
                  type="button"
                  onClick={() =>
                    setMenuOpen({ facilities: true, metadatas: false })
                  }
                  title={intl.formatMessage({ id: 'FACILITIES' })}
                />
              )}
            </div>
          </div>
          <div className="mt-3 col-12">
            <Map
              formik={formik}
              mapRef={mapRef}
              setController={setController}
              mapHeight="calc(60vh - 80px)"
            >
              {formik.errors['geoJson'] ? (
                <div
                  className="center-box d-flex flex-center"
                  style={{ pointerEvents: 'none' }}
                >
                  {formik.errors['geoJson']}
                </div>
              ) : null}
            </Map>
          </div>
        </div>
        <RemoveDialog
          open={open}
          onCancel={closeModal}
          onClose={closeModal}
          onConfirm={removeArea}
          name={area.name}
          message="ACTIONS.REMOVE.AREA.BODY"
          title="ACTIONS.REMOVE.AREA.TITLE"
        />
        {hasPermission('metadata*', 'R') && (
          <Drawer
            open={menuOpen.metadatas}
            onClose={closeSidebar}
            containerId="floormap-container"
          >
            <div className="flex-row-fluid h-100" style={{ width: 450 }}>
              <MetadataMenu
                intl={intl}
                hasPermission={hasPermission}
                type="area"
                id={id}
              />
            </div>
          </Drawer>
        )}
        {hasPermission('facilities*', 'R') && (
          <Drawer
            open={menuOpen.facilities}
            onClose={closeSidebar}
            containerId="floormap-container"
          >
            <div className="h-100" style={{ width: 460 }}>
              <div className="card h-100 px-0">
                <CardHeader
                  title={intl.formatMessage({ id: 'FACILITIES' })}
                  subtitle={intl.formatMessage(
                    { id: 'AREAS.FACILITIES.MENU.SUBTITLE' },
                    { name: area.name }
                  )}
                  iconPath={toAbsoluteUrl('/media/svg/senseworks/facility.svg')}
                  btn={
                    hasPermission('facilities*', 'C') && (
                      <AddButton
                        onClick={() =>
                          history.push(
                            `/geo-planner/areas/${id}/facilities/new`
                          )
                        }
                      />
                    )
                  }
                />
                <div className="d-flex flex-column h-100">
                  <div
                    className="px-3 py-2"
                    style={{ borderBottom: 'solid 1px #AAAAAA' }}
                  >
                    <SearchInput onChange={changeText} />
                  </div>
                  <div className="d-flex flex-column justify-content-between h-100">
                    <div>
                      {filteredFacilities.map(op => (
                        <MiniCard
                          key={op.id + op.name}
                          body={op.description}
                          title={op.name}
                          footer={`${op.locality ? op.locality + ',' : ''} ${
                            op.adminRegion
                          }${
                            op['countryISO3166-2']
                              ? ', ' + op['countryISO3166-2']
                              : ''
                          }`}
                          to={`/geo-planner/areas/${id}/facilities/${op.id}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Drawer>
        )}
      </div>
    </div>
  );
}

export default injectIntl(AreasEdit);
